import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider } from '@mui/material';

import type { FilterGroups } from 'src/components/app/context/FilterContext';
import { FilterProvider } from 'src/components/app/context/FilterContext';
import PageFrame from 'src/components/common/PageFrame';
import Header from 'src/components/app/Header';
import { MyFollowUpContextProvider } from 'src/components/myFollowUp/context/MyFollowUpContext';
import MyFollowUpSidebar from 'src/components/myFollowUp/MyFollowUpPage/Sidebar/MyFollowUpSidebar';

import FollowUpOverview from './FollowUpOverview/FollowUpOverview';
import { dateRangeToFilter, defaultDateRange } from './Sidebar/helper';

import { styles } from './MyFollowUpPage.styles';
import { styles as pageFrameStyles } from 'src/components/common/PageFrame/index.styles';

const initialFilterGroups: FilterGroups = {
  dateRange: [dateRangeToFilter(defaultDateRange)],
};

const useFilterSpecs = () => {
  const { t } = useTranslation('followUp');

  return useMemo(
    () => [
      {
        name: 'search',
        required: false,
        multiple: false,
        hideFilterChips: false,
      },
      {
        name: 'dateRange',
        required: false,
        multiple: true,
        hideFilterChips: false,
      },
      {
        name: 'survey',
        required: false,
        multiple: true,
        hideFilterChips: false,
      },
      {
        name: 'followUpOwner',
        required: false,
        multiple: true,
        hideFilterChips: false,
      },
      {
        name: 'status',
        required: false,
        multiple: true,
        hideFilterChips: false,
      },
    ],
    [t],
  );
};

const MyFollowUpPage: FC = () => (
  <FilterProvider
    allowCombinedFilters
    initialFilterGroups={initialFilterGroups}
    initialFilterSpecs={useFilterSpecs()}
  >
    <PageFrame
      sidebar={<MyFollowUpSidebar />}
      sx={styles.myFollowUpPage}
      header={
        <Header hideProjectLogo hidePreviousVersionBanner hideSurveySettings />
      }
    >
      <Divider sx={styles.divider} />
      <Box sx={pageFrameStyles.children()} data-test-id={'my-follow-up-page'}>
        <MyFollowUpContextProvider>
          <FollowUpOverview />
        </MyFollowUpContextProvider>
      </Box>
    </PageFrame>
  </FilterProvider>
);

export default MyFollowUpPage;
